import React, { useEffect, useState } from "react";
import { Client } from "../../interfaces/client";
import {
  getAdditionalFormTypes,
  uploadClientAdditionalForm,
} from "../../services/additionalFormService";
import { AdditionalFormType } from "../../interfaces/additionalFormType";
import { notifyAdminOfClientUpdate } from "../../services/emailService";

interface UploadAdditionalFormProps {
  client: Client;
  onClose: (message?: string) => void;
}

const UploadAdditionalForm: React.FC<UploadAdditionalFormProps> = ({
  client,
  onClose,
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [additionalFormTypes, setAdditionalFormTypes] = useState<
    AdditionalFormType[] | null
  >(null);
  const [selectedFormType, setSelectedFormType] =
    useState<AdditionalFormType | null>(null);

  useEffect(() => {
    const getDetails = async () => {
      try {
        //get additional form types
        const additionalFormTypesResponse = await getAdditionalFormTypes();
        setAdditionalFormTypes(additionalFormTypesResponse);
      } catch (error) {
      } finally {
      }
    };
    getDetails();
  }, []);

  const handleUploadAdditionalForm = async () => {
    if (!file || !selectedFormType) return;

    try {
      if (
        selectedFormType.additionalFormTypeID === 1 &&
        !file.type.startsWith("image/")
      ) {
        setErrorMessage("Signatures must be image type files.");
        return;
      }

      setLoadingSpinner(true);
      await uploadClientAdditionalForm(client, selectedFormType, file);
      onClose();
    } catch (error) {
      console.error("Error handling file upload:", error);
      setErrorMessage("Error uploading additional form.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  const handleGrabForm = async (type: number) => {
    try {
      const fileInput = document.createElement("input");
      fileInput.type = "file";
      if (type === 1 || type === 5) {
        fileInput.accept = ".png, .jpg, .jpeg, .bmp";
      } else {
        fileInput.accept = "image/*,.pdf";
      }
      fileInput.click();
      fileInput.onchange = async (event: any) => {
        const file = event.target.files[0];
        if (!file) return;
        setFile(file);
      };
      setErrorMessage(
        "If there is a form uploaded of the same type, you will be replacing it.",
      );
    } catch (error) {
      console.error("Error handling file upload:", error);
    } finally {
      setLoadingSpinner(false);
    }
  };

  return (
    <>
      <div className="background-mask" onClick={() => onClose()}></div>
      <div className="filter-popup">
        <div className="close-button-container">
          <button className="close-button" onClick={() => onClose()}>
            <i className="bi bi-x-circle"></i>
          </button>
        </div>
        <div className="filter-header">
          <h2>Upload Additional Form</h2>
          <div>Please choose a form type.</div>
        </div>
        <br></br>
        <div className="dialog-container">
          <div className="form-group">
            <select
              id="formTypeSelect"
              className="form-control"
              value={selectedFormType?.description || ""}
              onChange={(e) => {
                const selected = additionalFormTypes?.find(
                  (type) => type.description === e.target.value,
                );
                setSelectedFormType(selected || null);
              }}
            >
              <option value="" disabled>
                -- Select Form Type --
              </option>
              {additionalFormTypes?.map((formType) => (
                <option
                  key={formType.additionalFormTypeID}
                  value={formType.description}
                >
                  {formType.description}
                </option>
              ))}
            </select>
          </div>
          <div className="action-buttons">
            <button
              className="smbtn btn-grey"
              onClick={() =>
                handleGrabForm(
                  selectedFormType?.additionalFormTypeID
                    ? selectedFormType.additionalFormTypeID
                    : 0,
                )
              }
            >
              Upload Additional Form
            </button>
            {file && (
              <a
                href={URL.createObjectURL(file)}
                target="_blank"
                rel="noopener noreferrer"
              >
                View Form
                <i className="btn-icon bi bi-box-arrow-up-right"></i>
              </a>
            )}
          </div>

          {errorMessage && <div className="error">{errorMessage}</div>}
        </div>
        <div className="filter-actions">
          <button
            className={`smbtn btn-green ${
              !file || !selectedFormType ? "disabled" : ""
            }`}
            onClick={handleUploadAdditionalForm}
            disabled={!file || !selectedFormType}
          >
            Save to Client
          </button>
        </div>
      </div>
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </>
  );
};

export default UploadAdditionalForm;
